import React from "react";

type TextWithInlineTokensProps = {
    text?: string;
    /** Must contain exactly one capture group; its value is passed to `renderToken`. */
    tokenRegex: RegExp;
    renderToken: (capturedValue: string, key: string) => React.ReactNode;
    className?: string;
    enabled?: boolean;
};

/**
 * Renders `text` as a <p>, replacing every regex match with the output of `renderToken`.
 * When `enabled` is false (or no text), renders the text unchanged.
 */
export function TextWithInlineTokens({
    text,
    tokenRegex,
    renderToken,
    className,
    enabled = true,
}: TextWithInlineTokensProps) {
    if (!text || !enabled) {
        return <p className={className}>{text}</p>;
    }

    // Always work on a non-sticky, global clone so callers can pass shared regex literals safely.
    const regex = new RegExp(tokenRegex.source, tokenRegex.flags.includes("g") ? tokenRegex.flags : tokenRegex.flags + "g");

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let index = 0;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        parts.push(renderToken(match[1], `token-${index++}-${match.index}`));
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return <p className={className}>{parts}</p>;
}
